/* eslint-disable @typescript-eslint/camelcase */
import React, {
  useContext,
  useEffect,
  useRef,
  FunctionComponent,
  ReactNode,
  ReactText
} from 'react';

import { CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';
import languagesMap, { Locale } from '@components/common/language_menu/languagesMap';
import { ListOption } from '@components/library/dropdown/index';
import { TextWarningButton } from '@components/library/button';
import PrimaryInput from '@components/library/inputs/PrimaryInput';
import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';
import {
  ActionTypes,
  CustomFieldPayloadOperations,
  DeleteCustomFieldValuesDispatch,
  FlattenedCustomFieldKeyValuesMap,
  UpdateCustomFieldKeyDispatch,
  UpdateCustomFieldsDispatch,
  UpdateCustomFieldValuesDispatch,
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { RemoveValuesButton } from './RemoveValuesButton';
import {
  MultiValueFreeEntry,
  MultiValueRestricted,
  SingleValueFreeEntry,
  SingleValueRestricted
} from './custom-field-row-values';

interface CustomFieldKeyValueRowProps {
  controlledCustomFieldsEnabled: boolean;
  customFieldKey: CustomFieldKeysResponseObject;
  focused?: boolean;
  setNewCustomFieldFocus?: SetStateDispatch<boolean>;
}

interface CustomFieldKeyValueScopedContext {
  state: {
    editState: {
      flattenedCustomFieldKeyValuesMap: FlattenedCustomFieldKeyValuesMap;
    };
    ugtLocale: Locale | '';
  };
  dispatch:
    | DeleteCustomFieldValuesDispatch
    | UpdateCustomFieldKeyDispatch
    | UpdateCustomFieldsDispatch
    | UpdateCustomFieldValuesDispatch;
}

export const CustomFieldKeyValueRow: FunctionComponent<CustomFieldKeyValueRowProps> = ({
  controlledCustomFieldsEnabled,
  customFieldKey,
  focused,
  setNewCustomFieldFocus
}) => {
  const { state, dispatch } = useContext(assetModalContext);
  const keyInputRef = useRef(null);
  const { name, prioritized, allowed_values, restricted, multi_value_enabled } = customFieldKey.attributes;

  const existingCustomFieldMap = state?.editState?.flattenedCustomFieldKeyValuesMap?.[customFieldKey.id];

  useEffect(() => {
    if (focused) {
      keyInputRef.current.focus();
    }
  }, [focused]);

  const dispatchCustomFieldValue = (
    optionValue: ReactText,
    operation: CustomFieldPayloadOperations,
    customFieldValueId?: string
  ): void => {
    const cfv = existingCustomFieldMap?.customFieldValues.find(({ key }) => key === customFieldValueId);
    const customFieldValueDispatch = dispatch as UpdateCustomFieldValuesDispatch;
    customFieldValueDispatch({
      type: ActionTypes.UpdateCustomFieldValues,
      payload: {
        operation,
        customFieldKeyId: customFieldKey.id,
        customFieldValue: { key: '', ...cfv && cfv, value: optionValue.toString() }
      }
    });
  };

  const translationsWarning = (
    <div className="custom-field-values--translations">
      <span className="bff-warning" />
      <p className="custom-field-values--translations-hint">
        {bfTranslate("Unrestricted multi-value custom fields cannot be translated.")}
      </p>
    </div>
  );

  /**
   * https://brandfolder-team.atlassian.net/wiki/spaces/PROD/pages/1703084059/Multi-Value+Custom+Fields+UI+Behavior+Expectations
   * See above for guidance on the different scenarios that must be supported by custom fields
   */
  const getCustomFieldValueComponent = (): ReactNode => {
    const selectedLanguage = languagesMap.find((langDetails) => langDetails.locale === state.ugtLocale);
    const translating = selectedLanguage
      && state.ugtLocale !== BFG.locales.ugtLocaleDefault
      && BFG.context.hasFeature("translations");

    if (controlledCustomFieldsEnabled && restricted) {
      const options: ListOption[] = allowed_values.map((allowedValue) => (
        { label: allowedValue, value: allowedValue }
      ));
      const currValues = options.filter((opt) => (
        existingCustomFieldMap?.customFieldValues?.some((cfv) => cfv.value === opt.value)
      ));
      if (multi_value_enabled) {
        if (translating) {
          return translationsWarning;
        }

        return (
          <MultiValueRestricted
            currValues={currValues}
            dispatchCustomFieldValue={dispatchCustomFieldValue}
            existingCustomFieldMap={existingCustomFieldMap}
            options={options}
          />
        );
      }

      return (
        <SingleValueRestricted
          currValues={currValues}
          dispatchCustomFieldValue={dispatchCustomFieldValue}
          existingCustomFieldMap={existingCustomFieldMap}
          options={options}
        />
      );
    }

    if (multi_value_enabled) {
      if (translating) {
        return translationsWarning;
      }

      return (
        <MultiValueFreeEntry
          dispatchCustomFieldValue={dispatchCustomFieldValue}
          existingCustomFieldMap={existingCustomFieldMap}
        />
      );
    }

    return (
      <SingleValueFreeEntry
        customFieldKeyId={customFieldKey.id}
        dispatchCustomFieldValue={dispatchCustomFieldValue}
        existingCustomFieldMap={existingCustomFieldMap}
      />
    );
  };

  const getKeyComponent = (): ReactNode => {
    if (customFieldKey.id.includes('create-cfk')) {
      return (
        <PrimaryInput
          attributes={{
            'aria-label': "Custom Field Key",
            className: "custom-field-primary-input",
            name: `custom-field-input--${customFieldKey.id}`,
            onChange: (e): void => {
              if (setNewCustomFieldFocus) {
                setNewCustomFieldFocus(false);
              }
              const customFieldValueDispatch = dispatch as UpdateCustomFieldKeyDispatch;
              customFieldValueDispatch({
                type: ActionTypes.UpdateCustomFieldKey,
                payload: {
                  customFieldKeyId: customFieldKey.id,
                  customFieldKeyName: (e as InputChangeEvent).target.value
                }
              });
            },
            placeholder: bfTranslate('Key'),
            ref: keyInputRef,
            value: name
          }}
          input={{ value: name }}
        />
      );
    }

    if (customFieldKey.attributes.multi_value_enabled && !customFieldKey.attributes.restricted) {
      return (
        <div>
          <span>{customFieldKey.attributes.name}</span>
          <p className="custom-field-key--value-separation-hint">{bfTranslate('Separate values with commas')}</p>
        </div>
      );
    }

    return customFieldKey.attributes.name;
  };

  return (
    <li key={`custom-field-row-${customFieldKey.id}`} className="custom-field-row custom-field-row__new">
      <span className="custom-field-key--container">
        <span
          className={`custom-field-key ${prioritized ? 'prioritized' : ''}`}
        >
          {getKeyComponent()}
        </span>
      </span>
      <div className="custom-field-value--container">
        {getCustomFieldValueComponent()}
        <div className="custom-field-values--remove-buttons">
          {customFieldKey.id.includes('create-cfk') ? (
            <TextWarningButton
              className="custom-field-values--remove"
              icon="bff-trash"
              onClick={(): void => {
                const deleteDispatch = dispatch as DeleteCustomFieldValuesDispatch;
                deleteDispatch({
                  type: ActionTypes.DeleteCustomFieldValues,
                  payload: {
                    customFieldKeyId: customFieldKey.id,
                    deleteKey: true
                  }
                });
              }}
            >
              {bfTranslate("Remove row")}
            </TextWarningButton>
          ) : (
            <RemoveValuesButton
              isSingular={existingCustomFieldMap?.customFieldValues?.length === 1}
              isVisible={
                existingCustomFieldMap?.customFieldValues?.length > 0
                && existingCustomFieldMap.customFieldValues[0].value !== ''
              }
              onClick={(): void => {
                const deleteDispatch = dispatch as DeleteCustomFieldValuesDispatch;
                deleteDispatch({
                  type: ActionTypes.DeleteCustomFieldValues,
                  payload: {
                    customFieldKeyId: customFieldKey.id
                  }
                });
              }}
            />
          )}
        </div>
      </div>
    </li>
  );
};
